import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { Row, FormControl, Grid, Panel } from 'react-bootstrap';

import { useAlertStore } from '../../store/providers/Alert';
import { useFranchiseAccountingStore } from '../../store/providers/FranchiseAccounting';
import { useNetSuiteAllItemsStore } from '../../store/providers/NetSuiteAllItems';
import Permission from '../../components/Permission/Permission';

import Spinner from '../../components/Spinner/Spinner';
import OptionsHelper from '../../lib/OptionsHelper';
import * as constants from '../../constants';
import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';
import SectionHeader from '../../components/SectionHeader/SectionHeader';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import SideMenu from '../../components/SideMenu/SideMenu';
import PageHeader from '../../components/PageHeader/PageHeader';

const InputCollection = React.lazy(() => import('../../components/InputCollection/InputCollection'));

function ProductAccounts() {
    // Redux state (This is only temporary until the Edit page is converted to HOOKS)
    const dispatch = useDispatch();

    const { franchise } = useSelector(state => ({
        franchise: state.franchise.franchise
    }));

    // Store state
    const { addAlert } = useAlertStore();
    const { listRecords, createNSRecord, data, clearData } = useFranchiseAccountingStore();
    const { listRecords: netSuiteItemsList, data: netSuiteItemsData } = useNetSuiteAllItemsStore();

    // Local state
    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isUpdated, setIsUpdated] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [listData, setListData] = useState([]);
    const [netSuiteItemsOptions, setNetSuiteItemsOptions] = useState([]);
    const [hasNetSuite, setHasNetSuite] = useState(false);

    const pagePermission = constants.PERMISSION_FRANCHISES_COMMISSIONS_TRANSACTIONFEES;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const accountingFeeType = {
        LookupTypes: "LookupTypes",
        FranchiseTransactionFee: "FranchiseTransactionFee",
        PaymentSubscriptionCategories: "PaymentSubscriptionCategories",
        FranchiseAgentFee: "FranchiseAgentFee",
        FranchiseProducts: "FranchiseProducts",
        AgentProducts: "AgentProducts",
        RoyaltyItems: "RoyaltyItems",
    };

    const changedhandler = (row, value, corp) => {
        if(corp) {
            row.netsuiteNonCorpItemId = value;
        } else {
            row.netsuiteItemId = value;
        }
        row.Corp = corp;
        createNSRecord(row);
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords({ franchiseId: 0 });
            }
        
            if (data.status === 'success' && data.scope === 'list') {
                const { hasNetSuite, netSuiteFeeItemMappings } = data.body;
                setHasNetSuite(hasNetSuite);
                setListData(netSuiteFeeItemMappings);
            }
        }

        setIsBusy(!_.isEmpty(data) && (data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'  || data.scope === 'create_netsuitemapping')));
    }, [data, addAlert, clearData, listRecords, franchise]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'franchise-Accounting', text: 'Accounting', path: window.location.pathname }));

        listRecords({ franchiseId: 0 });
        netSuiteItemsList({ franchiseId: "" });
        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch, franchise, netSuiteItemsList]);

    useEffect(() => {
        if (!isUpdated) { return; }

        listRecords({ franchiseId: 0 });
        setIsUpdated(false);
    }, [isUpdated, netSuiteItemsList]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(netSuiteItemsData) && netSuiteItemsData.scope === 'list' && netSuiteItemsData.status === 'success') {
            let optionvals = netSuiteItemsData.body.map(option => { return { id: option.netSuiteId, name: option.name } })
            setNetSuiteItemsOptions(OptionsHelper({ list: optionvals, includeBlank: true }));
            setIsLoaded(true);
        }
    }, [netSuiteItemsData]);

    return (
        <>
            <SideMenu activeKey="accounting" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted && 
                    <div style={{ paddingRight: '15px' }}>
                        <PageHeader title="NetSuite Mapping" />
                        <Panel>
                            <Panel.Body>
                                <Suspense fallback={<Spinner text="Loading..." />}>
                                    {
                                        !isLoaded
                                            ? <Spinner text="Loading..." />
                                            : <>
                                                {
                                                    hasNetSuite ? <SectionHeader title={hasNetSuite ? "NetSuite Integration" : "QuickBooks Integration"} /> : null
                                                }
                                                { 
                                                <Grid fluid className="accounting-tab-details">
                                                    <Row style={{ marginBottom: '8px' }}>
                                                        <div className="col-md-2">
                                                        </div>
                                                        <div className="col-md-3">
                                                            <strong>FM Fee Type</strong>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <strong>Corp NetSuite Item</strong>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <strong>Non Corp NetSuite Item</strong>
                                                        </div>
                                                    </Row>
                                                    <Row className="title">
                                                        <div className="col-md-6">
                                                            <h4>Transaction Fees</h4>
                                                        </div>
                                                    </Row>
                                                    {listData.map(datavals =>
                                                        (datavals.fromTable === accountingFeeType.LookupTypes) &&
                                                        
                                                        <Row key={datavals.itemId}>
                                                            <div className="col-md-2">
                                                            </div>
                                                            <div className="col-md-3">
                                                                {datavals.description}
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 0)} value={datavals.netsuiteItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 1)} value={datavals.netsuiteNonCorpItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                        </Row>
                                                    )}
                                                    {
                                                        listData.filter(datavals => (datavals.fromTable === accountingFeeType.FranchiseTransactionFee)).length > 0 &&
                                                        <Row className="title">
                                                            <div className="col-md-6">
                                                                <h4>Additional Transaction Fees</h4>
                                                            </div>
                                                        </Row>
                                                    }
                                                    {
                                                        listData.map(datavals =>
                                                            (datavals.fromTable === accountingFeeType.FranchiseTransactionFee) &&
                                                            <Row key={datavals.itemId}>
                                                                <div className="col-md-2">

                                                                </div>
                                                                <div className="col-md-3">
                                                                    {datavals.description}
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <InputCollection>
                                                                        <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 0)} value={datavals.netsuiteItemId || ''} disabled={!canEdit || isBusy}>
                                                                            {netSuiteItemsOptions}
                                                                        </FormControl>
                                                                    </InputCollection>
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <InputCollection>
                                                                        <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 1)} value={datavals.netsuiteNonCorpItemId || ''} disabled={!canEdit || isBusy}>
                                                                            {netSuiteItemsOptions}
                                                                        </FormControl>
                                                                    </InputCollection>
                                                                </div>
                                                            </Row>
                                                        )
                                                    }
                                                    <Row className="title">
                                                        <div className="col-md-6">
                                                            <h4>Agent Fees</h4>
                                                        </div>
                                                    </Row>
                                                    {listData.map(datavals =>
                                                        (datavals.fromTable === accountingFeeType.PaymentSubscriptionCategories) &&
                                                        <Row key={datavals.itemId}>
                                                            <div className="col-md-2">

                                                            </div>
                                                            <div className="col-md-3">
                                                                {datavals.description}
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 0)} value={datavals.netsuiteItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 1)} value={datavals.netsuiteNonCorpItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                        </Row>
                                                    )}
                                                    {
                                                        listData.filter(datavals => (datavals.fromTable === accountingFeeType.FranchiseAgentFee)).length > 0 &&
                                                        <Row className="title">
                                                            <div className="col-md-6">
                                                                <h4>Additional Agent Fees</h4>
                                                            </div>
                                                        </Row>
                                                    }
                                                    {listData.map(datavals =>
                                                        (datavals.fromTable === accountingFeeType.FranchiseAgentFee) &&
                                                        <Row key={datavals.itemId}>
                                                            <div className="col-md-2">

                                                            </div>
                                                            <div className="col-md-3">
                                                                {datavals.description}
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 0)} value={datavals.netsuiteItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 1)} value={datavals.netsuiteNonCorpItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                        </Row>
                                                    )}
                                                    {
                                                        listData.filter(datavals => (datavals.fromTable === accountingFeeType.RoyaltyItems)).length > 0 &&
                                                        <Row className="title">
                                                            <div className="col-md-6">
                                                                <h4>Royalty Items</h4>
                                                            </div>
                                                        </Row>
                                                    }
                                                    {listData.map(datavals =>
                                                        (datavals.fromTable === accountingFeeType.RoyaltyItems) &&
                                                        <Row key={datavals.itemId}>
                                                            <div className="col-md-2">

                                                            </div>
                                                            <div className="col-md-3">
                                                                {datavals.description}
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 0)} value={datavals.netsuiteItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 1)} value={datavals.netsuiteNonCorpItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                        </Row>
                                                    )}
                                                    {
                                                        listData.filter(datavals => (datavals.fromTable === accountingFeeType.FranchiseProducts)).length > 0 &&
                                                        <Row className="title">
                                                            <div className="col-md-6">
                                                                <h4>Instance Subscription</h4>
                                                            </div>
                                                        </Row>
                                                    }
                                                    {listData.map(datavals =>
                                                        (datavals.fromTable === accountingFeeType.FranchiseProducts) &&
                                                        <Row key={datavals.itemId}>
                                                            <div className="col-md-2">

                                                            </div>
                                                            <div className="col-md-3">
                                                                {datavals.description}
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 0)} value={datavals.netsuiteItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 1)} value={datavals.netsuiteNonCorpItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                        </Row>
                                                    )}
                                                    {
                                                        listData.filter(datavals => (datavals.fromTable === accountingFeeType.AgentProducts)).length > 0 &&
                                                        <Row className="title">
                                                            <div className="col-md-6">
                                                                <h4>Agent Subscription</h4>
                                                            </div>
                                                        </Row>
                                                    }
                                                    {listData.map(datavals =>
                                                        (datavals.fromTable === accountingFeeType.AgentProducts) &&
                                                        <Row key={datavals.itemId}>
                                                            <div className="col-md-2">

                                                            </div>
                                                            <div className="col-md-3">
                                                                {datavals.description}
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 0)} value={datavals.netsuiteItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <InputCollection>
                                                                    <FormControl id="statusId" componentClass="select" onChange={(e) => changedhandler(datavals, e.target.value, 1)} value={datavals.netsuiteNonCorpItemId || ''} disabled={!canEdit || isBusy}>
                                                                        {netSuiteItemsOptions}
                                                                    </FormControl>
                                                                </InputCollection>
                                                            </div>
                                                        </Row>
                                                    )}                                                    
                                                </Grid>
                                                }
                                            </>
                                    }
                                </Suspense>
                            </Panel.Body>
                        </Panel>
                    </div>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(ProductAccounts));